import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Save, Loader2, X, Plus, ImageIcon, Link as LinkIcon, Globe,
  AlertCircle, Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import MediaPicker from '../media/MediaPicker';

/**
 * PageSeoPanel — editor for a SitePage's `seo` sub-object.
 *
 * Shape expected:
 *   seo: {
 *     title: { ar, en },
 *     description: { ar, en },
 *     keywords: string[],
 *     ogImageUrl: string,
 *     canonicalUrl: string,
 *     noindex: boolean,
 *     priority: number|null,       // 0..1 (sitemap)
 *     changefreq: string|null,     // 'daily'|'weekly'|...
 *   }
 *
 * Props:
 *   value     — current seo object (from draftSnapshot.seo)
 *   pageLabel — readable label of the page being edited (shown in header)
 *   pageKey   — optional: the pageKey identifier (for clarity)
 *   dirty     — bool; whether the value differs from what's persisted
 *   saving    — bool; disables save while in flight
 *   onChange  — called on every edit with the new seo object
 *   onSave    — called when the user clicks "Save SEO draft"
 *
 * The panel is purely controlled — it doesn't track dirty state internally.
 * The parent (WebsiteBuilder) compares `value` against the server draft to
 * decide whether the Save button should be enabled.
 */

const CHANGEFREQ_OPTIONS = [
  { value: '',        label: 'الافتراضي من إعدادات الموقع' },
  { value: 'always',  label: 'always' },
  { value: 'hourly',  label: 'hourly' },
  { value: 'daily',   label: 'daily' },
  { value: 'weekly',  label: 'weekly' },
  { value: 'monthly', label: 'monthly' },
  { value: 'yearly',  label: 'yearly' },
  { value: 'never',   label: 'never' },
];

const DEFAULT_SEO = {
  title: { ar: '', en: '' },
  description: { ar: '', en: '' },
  keywords: [],
  ogImageUrl: '',
  canonicalUrl: '',
  noindex: false,
  priority: null,
  changefreq: null,
};

function normaliseSeo(v) {
  const out = { ...DEFAULT_SEO };
  if (!v || typeof v !== 'object') return out;
  out.title = typeof v.title === 'object' && v.title ? { ar: v.title.ar || '', en: v.title.en || '' } : { ar: '', en: '' };
  out.description = typeof v.description === 'object' && v.description ? { ar: v.description.ar || '', en: v.description.en || '' } : { ar: '', en: '' };
  out.keywords = Array.isArray(v.keywords) ? v.keywords.filter(Boolean) : [];
  out.ogImageUrl = v.ogImageUrl || '';
  out.canonicalUrl = v.canonicalUrl || '';
  out.noindex = !!v.noindex;
  out.priority = (v.priority === null || v.priority === undefined || v.priority === '') ? null : Number(v.priority);
  out.changefreq = v.changefreq || null;
  return out;
}

export default function PageSeoPanel({ value, pageLabel, pageKey, dirty, saving, onChange, onSave }) {
  const seo = useMemo(() => normaliseSeo(value), [value]);
  const [newKeyword, setNewKeyword] = useState('');
  const [activeLocale, setActiveLocale] = useState('ar');
  const [pickerOpen, setPickerOpen] = useState(false);

  const titleLen = (seo.title[activeLocale] || '').length;
  const descLen = (seo.description[activeLocale] || '').length;

  const set = useCallback((patch) => {
    onChange({ ...seo, ...patch });
  }, [onChange, seo]);

  const setLocalisedField = useCallback((field, locale, v) => {
    set({ [field]: { ...seo[field], [locale]: v } });
  }, [set, seo]);

  const addKeyword = useCallback((raw) => {
    const kw = String(raw || '').trim();
    if (!kw) return;
    if (seo.keywords.includes(kw)) return;
    if (seo.keywords.length >= 25) {
      toast.error('الحد الأقصى 25 كلمة مفتاحية');
      return;
    }
    set({ keywords: [...seo.keywords, kw] });
    setNewKeyword('');
  }, [seo.keywords, set]);

  const removeKeyword = useCallback((kw) => {
    set({ keywords: seo.keywords.filter((k) => k !== kw) });
  }, [seo.keywords, set]);

  // Reset the add-keyword input when the page changes
  useEffect(() => { setNewKeyword(''); }, [pageKey]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
      dir="rtl"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-100 text-emerald-600">
            <Search className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-800">إعدادات SEO للصفحة</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {pageLabel ? `الصفحة: ${pageLabel}` : 'عدّل بيانات محرّكات البحث لهذه الصفحة فقط'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          حفظ SEO كمسودة
        </button>
      </div>

      {/* Body */}
      <div className="p-5 space-y-6">
        {/* Locale tabs */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            {[
              { value: 'ar', label: 'العربية' },
              { value: 'en', label: 'English' },
            ].map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => setActiveLocale(l.value)}
                className={[
                  'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                  activeLocale === l.value ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                ].join(' ')}
              >
                {l.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-gray-400">
            <Info className="w-3 h-3 inline-block ml-1" />
            العناوين والأوصاف ثنائية اللغة
          </span>
        </div>

        {/* Title */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-gray-700">عنوان الصفحة ({activeLocale === 'ar' ? 'العربية' : 'English'})</label>
            <span className={[
              'text-[11px] font-mono',
              titleLen > 65 ? 'text-rose-500' : titleLen > 55 ? 'text-amber-500' : 'text-gray-400',
            ].join(' ')}>
              {titleLen}/60
            </span>
          </div>
          <input
            value={seo.title[activeLocale] || ''}
            onChange={(e) => setLocalisedField('title', activeLocale, e.target.value)}
            placeholder={activeLocale === 'ar' ? 'مثال: تحاليل الدم الشاملة — مختبرات أكمي' : 'e.g. Comprehensive Blood Tests — Acme Labs'}
            dir={activeLocale === 'en' ? 'ltr' : 'rtl'}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">يُستخدم في علامة التبويب وفي Google. 50-60 حرف مثالي.</p>
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-gray-700">الوصف الوصفي ({activeLocale === 'ar' ? 'العربية' : 'English'})</label>
            <span className={[
              'text-[11px] font-mono',
              descLen > 170 ? 'text-rose-500' : descLen > 150 ? 'text-amber-500' : 'text-gray-400',
            ].join(' ')}>
              {descLen}/160
            </span>
          </div>
          <textarea
            value={seo.description[activeLocale] || ''}
            onChange={(e) => setLocalisedField('description', activeLocale, e.target.value)}
            placeholder={activeLocale === 'ar' ? 'وصف جذّاب للصفحة يظهر أسفل العنوان في نتائج البحث.' : 'A compelling summary that appears under the title in search results.'}
            dir={activeLocale === 'en' ? 'ltr' : 'rtl'}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">150-160 حرف مثالي. تجنّب الحشو.</p>
        </div>

        {/* Keywords */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">الكلمات المفتاحية</label>
          <div className="flex flex-wrap gap-2 items-center p-2 rounded-xl border border-gray-200 min-h-[44px] bg-white">
            {seo.keywords.map((kw) => (
              <span key={kw} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                {kw}
                <button
                  type="button"
                  onClick={() => removeKeyword(kw)}
                  className="p-0.5 rounded hover:bg-blue-100"
                  title="إزالة"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <div className="flex items-center gap-1 flex-1 min-w-[180px]">
              <input
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                    e.preventDefault();
                    addKeyword(newKeyword);
                  }
                }}
                placeholder="أضف كلمة ثم Enter"
                className="flex-1 px-2 py-1 bg-transparent outline-none text-sm placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => addKeyword(newKeyword)}
                disabled={!newKeyword.trim()}
                className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40"
                title="إضافة"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">الحد الأقصى 25 كلمة. تُستخدم كمرجع داخلي فقط (Google يتجاهلها منذ 2009).</p>
        </div>

        {/* OG image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">صورة OpenGraph (تُعرض في مشاركات السوشيال)</label>
          {seo.ogImageUrl ? (
            <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
              <img src={seo.ogImageUrl} alt="og" className="w-20 h-20 object-cover rounded-lg shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 font-mono truncate" dir="ltr">{seo.ogImageUrl}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-blue-300 hover:text-blue-700"
                  >
                    تغيير
                  </button>
                  <button
                    type="button"
                    onClick={() => set({ ogImageUrl: '' })}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 hover:border-rose-300 hover:text-rose-600"
                  >
                    إزالة
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40 transition-colors text-sm text-gray-500"
            >
              <ImageIcon className="w-6 h-6 text-gray-400" />
              اختر صورة من المكتبة
            </button>
          )}
          <p className="text-xs text-gray-400 mt-1">يُنصح بمقاس 1200×630 بيكسل.</p>
        </div>

        {/* Canonical URL */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
            <LinkIcon className="w-3.5 h-3.5 text-gray-400" />
            Canonical URL
          </label>
          <input
            value={seo.canonicalUrl}
            onChange={(e) => set({ canonicalUrl: e.target.value })}
            placeholder="اتركه فارغاً لاستخدام الرابط الحالي"
            dir="ltr"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">
            مفيد لو هذه الصفحة نسخة من صفحة أخرى. يخبر Google بالصفحة الأصلية لتجنّب duplicate content.
          </p>
        </div>

        {/* Index toggle + sitemap priority + changefreq */}
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl border border-gray-200 hover:border-rose-200 hover:bg-rose-50/40 transition-colors">
            <input
              type="checkbox"
              checked={seo.noindex}
              onChange={(e) => set({ noindex: e.target.checked })}
              className="w-5 h-5 text-rose-600 rounded mt-0.5"
            />
            <div>
              <span className="font-medium text-gray-800 text-sm flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
                Noindex
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                منع محرّكات البحث من فهرسة هذه الصفحة.
              </p>
            </div>
          </label>

          <div className="p-3 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sitemap Priority
            </label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={seo.priority ?? 0.5}
                onChange={(e) => set({ priority: Number(e.target.value) })}
                className="flex-1"
              />
              <span className="text-xs font-mono text-gray-500 w-8 text-center">
                {seo.priority ?? '—'}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">أعلى قيمة = أهمية أعلى.</p>
          </div>

          <div className="p-3 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sitemap Changefreq</label>
            <select
              value={seo.changefreq || ''}
              onChange={(e) => set({ changefreq: e.target.value || null })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {CHANGEFREQ_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Preview (Google SERP style) */}
        <div className="p-4 rounded-xl border border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
            <Globe className="w-3.5 h-3.5" />
            معاينة تقريبية في Google
          </div>
          <div className="space-y-1" dir="rtl">
            <div className="text-sm text-green-800 truncate">https://your-site.listechs.com/{pageKey}</div>
            <div className="text-[17px] text-blue-700 truncate leading-tight">
              {seo.title[activeLocale] || '(بدون عنوان)'}
            </div>
            <div className="text-xs text-gray-600 line-clamp-2 leading-snug">
              {seo.description[activeLocale] || '(بدون وصف)'}
            </div>
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(asset) => {
          set({ ogImageUrl: asset?.publicUrl || asset?.url || '' });
          setPickerOpen(false);
        }}
        folder="sections"
      />
    </motion.div>
  );
}
